import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { UserPlus, Shield, PencilSimple } from '@phosphor-icons/react';

export default function AdminUsers() {
  const [admins, setAdmins] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // the admin object being edited, or null for new
  const [form, setForm] = useState({ name: '', email: '', mobile: '', password: '' });
  const [loading, setLoading] = useState(false);

  const fetchAdmins = async () => {
    try {
      const { data } = await api.get('/admin/admin-users');
      setAdmins(data);
    } catch {}
  };

  useEffect(() => { fetchAdmins(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', email: '', mobile: '', password: '' });
    setDialogOpen(true);
  };

  const openEdit = (admin) => {
    setEditing(admin);
    setForm({
      name: admin.name || '',
      email: admin.email || '',
      mobile: admin.mobile || '',
      password: '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        const payload = {
          name: form.name,
          email: form.email,
          mobile: form.mobile,
        };
        if (form.password) payload.password = form.password;
        // Admin _id stored as Mongo ObjectId; we use it as `id` after list transform
        const id = editing.id || editing._id;
        await api.put(`/admin/admin-users/${id}`, payload);
      } else {
        await api.post('/admin/admin-users', form);
      }
      await fetchAdmins();
      setDialogOpen(false);
      setEditing(null);
      setForm({ name: '', email: '', mobile: '', password: '' });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-users-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-[#0A0A0A]">Admin Users</h1>
          <p className="text-[#666] text-xs uppercase tracking-widest">Manage administrator accounts · Login by mobile number</p>
        </div>
        <Button onClick={openNew} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" data-testid="add-admin-btn">
          <UserPlus size={16} className="mr-2" /> Add Admin
        </Button>
      </div>

      <div className="bg-white border border-[#E5E5E5]">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F3F3F3]">
              <TableHead className="text-xs uppercase tracking-widest">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Mobile (Login)</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Email</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {admins.map((a, i) => (
              <TableRow key={a.id || a._id || i} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''}>
                <TableCell className="font-medium flex items-center gap-2">
                  <Shield size={14} className="text-[#002FA7]" /> {a.name}
                </TableCell>
                <TableCell className="font-mono text-sm">{a.mobile || '-'}</TableCell>
                <TableCell>{a.email || '-'}</TableCell>
                <TableCell>
                  <span className="text-xs uppercase tracking-widest px-2 py-1 bg-[#00C853]/10 text-[#00C853]">Active</span>
                </TableCell>
                <TableCell className="text-right">
                  <button
                    onClick={() => openEdit(a)}
                    className="p-2 hover:bg-[#F3F3F3]"
                    title="Edit"
                    data-testid={`edit-admin-${a.id || a.mobile}`}
                  >
                    <PencilSimple size={16} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {admins.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-[#666] py-8">No admin users</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add / Edit Admin Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Admin User' : 'Add Admin User'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Name *</Label>
              <Input data-testid="admin-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="rounded-none" placeholder="Full name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Mobile * (Login username &amp; WhatsApp)</Label>
              <Input data-testid="admin-mobile-input" value={form.mobile} onChange={e => setForm({...form, mobile: e.target.value})} required className="rounded-none" placeholder="10-digit mobile" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Email (Optional)</Label>
              <Input data-testid="admin-email-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="rounded-none" placeholder="admin@company.com" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">
                {editing ? 'New Password (leave blank to keep current)' : 'Password *'}
              </Label>
              <Input data-testid="admin-password-input" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required={!editing} className="rounded-none" placeholder={editing ? 'Enter new password to change' : 'Min 6 characters'} />
            </div>
            <Button data-testid="admin-save-btn" type="submit" disabled={loading} className="w-full bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">
              {loading ? (editing ? 'Saving...' : 'Creating...') : (editing ? 'Save Changes' : 'Create Admin')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
